import { createContext, useContext, useState, useRef, useEffect } from "react";
import { g as getDefaults, a as getI18n } from "./i18nInstance-89725fcb.js";
function warn() {
  if (console && console.warn) {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++)
      args[_key] = arguments[_key];
    typeof args[0] == "string" && (args[0] = `react-i18next:: ${args[0]}`), console.warn(...args);
  }
}
const alreadyWarned = {};
function warnOnce() {
  for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++)
    args[_key2] = arguments[_key2];
  typeof args[0] == "string" && alreadyWarned[args[0]] || (typeof args[0] == "string" && (alreadyWarned[args[0]] = /* @__PURE__ */ new Date()), warn(...args));
}
const loadedClb = (i18n, cb) => () => {
  if (i18n.isInitialized)
    cb();
  else {
    const initialized = () => {
      setTimeout(() => {
        i18n.off("initialized", initialized);
      }, 0), cb();
    };
    i18n.on("initialized", initialized);
  }
};
function loadNamespaces(i18n, ns, cb) {
  i18n.loadNamespaces(ns, loadedClb(i18n, cb));
}
function loadLanguages(i18n, lng, ns, cb) {
  typeof ns == "string" && (ns = [ns]), ns.forEach((n) => {
    i18n.options.ns.indexOf(n) < 0 && i18n.options.ns.push(n);
  }), i18n.loadLanguages(lng, loadedClb(i18n, cb));
}
function oldI18nextHasLoadedNamespace(ns, i18n) {
  let options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
  const lng = i18n.languages[0], fallbackLng = i18n.options ? i18n.options.fallbackLng : !1, lastLng = i18n.languages[i18n.languages.length - 1];
  if (lng.toLowerCase() === "cimode")
    return !0;
  const loadNotPending = (l, n) => {
    const loadState = i18n.services.backendConnector.state[`${l}|${n}`];
    return loadState === -1 || loadState === 2;
  };
  return options.bindI18n && options.bindI18n.indexOf("languageChanging") > -1 && i18n.services.backendConnector.backend && i18n.isLanguageChangingTo && !loadNotPending(i18n.isLanguageChangingTo, ns) ? !1 : !!(i18n.hasResourceBundle(lng, ns) || !i18n.services.backendConnector.backend || i18n.options.resources && !i18n.options.partialBundledLanguages || loadNotPending(lng, ns) && (!fallbackLng || loadNotPending(lastLng, ns)));
}
function hasLoadedNamespace(ns, i18n) {
  let options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
  return !i18n.languages || !i18n.languages.length ? (warnOnce("i18n.languages were undefined or empty", i18n.languages), !0) : i18n.options.ignoreJSONStructure !== void 0 ? i18n.hasLoadedNamespace(ns, {
    lng: options.lng,
    precheck: (i18nInstance, loadNotPending) => {
      if (options.bindI18n && options.bindI18n.indexOf("languageChanging") > -1 && i18nInstance.services.backendConnector.backend && i18nInstance.isLanguageChangingTo && !loadNotPending(i18nInstance.isLanguageChangingTo, ns))
        return !1;
    }
  }) : oldI18nextHasLoadedNamespace(ns, i18n, options);
}
const I18nContext = createContext();
class ReportNamespaces {
  constructor() {
    this.usedNamespaces = {};
  }
  addUsedNamespaces(namespaces) {
    namespaces.forEach((ns) => {
      this.usedNamespaces[ns] || (this.usedNamespaces[ns] = !0);
    });
  }
  getUsedNamespaces() {
    return Object.keys(this.usedNamespaces);
  }
}
const usePrevious = (value, ignore) => {
  const ref = useRef();
  return useEffect(() => {
    ref.current = ignore ? ref.current : value;
  }, [value, ignore]), ref.current;
};
function useTranslation(ns) {
  let props = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const {
    i18n: i18nFromProps
  } = props, {
    i18n: i18nFromContext,
    defaultNS: defaultNSFromContext
  } = useContext(I18nContext) || {}, i18n = i18nFromProps || i18nFromContext || getI18n();
  if (i18n && !i18n.reportNamespaces && (i18n.reportNamespaces = new ReportNamespaces()), !i18n) {
    warnOnce("You will need to pass in an i18next instance by using initReactI18next");
    const notReadyT = (k, optsOrDefaultValue) => typeof optsOrDefaultValue == "string" ? optsOrDefaultValue : optsOrDefaultValue && typeof optsOrDefaultValue == "object" && typeof optsOrDefaultValue.defaultValue == "string" ? optsOrDefaultValue.defaultValue : Array.isArray(k) ? k[k.length - 1] : k, retNotReady = [notReadyT, {}, !1];
    return retNotReady.t = notReadyT, retNotReady.i18n = {}, retNotReady.ready = !1, retNotReady;
  }
  i18n.options.react && i18n.options.react.wait !== void 0 && warnOnce("It seems you are still using the old wait option, you may migrate to the new useSuspense behaviour.");
  const i18nOptions = {
    ...getDefaults(),
    ...i18n.options.react,
    ...props
  }, {
    useSuspense,
    keyPrefix
  } = i18nOptions;
  let namespaces = ns || defaultNSFromContext || i18n.options && i18n.options.defaultNS;
  namespaces = typeof namespaces == "string" ? [namespaces] : namespaces || ["translation"], i18n.reportNamespaces.addUsedNamespaces && i18n.reportNamespaces.addUsedNamespaces(namespaces);
  const ready = (i18n.isInitialized || i18n.initializedStoreOnce) && namespaces.every((n) => hasLoadedNamespace(n, i18n, i18nOptions));
  function getT() {
    return i18n.getFixedT(props.lng || null, i18nOptions.nsMode === "fallback" ? namespaces : namespaces[0], keyPrefix);
  }
  const [t, setT] = useState(getT);
  let joinedNS = namespaces.join();
  props.lng && (joinedNS = `${props.lng}${joinedNS}`);
  const previousJoinedNS = usePrevious(joinedNS), isMounted = useRef(!0);
  useEffect(() => {
    const {
      bindI18n,
      bindI18nStore
    } = i18nOptions;
    isMounted.current = !0, !ready && !useSuspense && (props.lng ? loadLanguages(i18n, props.lng, namespaces, () => {
      isMounted.current && setT(getT);
    }) : loadNamespaces(i18n, namespaces, () => {
      isMounted.current && setT(getT);
    })), ready && previousJoinedNS && previousJoinedNS !== joinedNS && isMounted.current && setT(getT);
    function boundReset() {
      isMounted.current && setT(getT);
    }
    return bindI18n && i18n && i18n.on(bindI18n, boundReset), bindI18nStore && i18n && i18n.store.on(bindI18nStore, boundReset), () => {
      isMounted.current = !1, bindI18n && i18n && bindI18n.split(" ").forEach((e) => i18n.off(e, boundReset)), bindI18nStore && i18n && bindI18nStore.split(" ").forEach((e) => i18n.store.off(e, boundReset));
    };
  }, [i18n, joinedNS]);
  const isInitial = useRef(!0);
  useEffect(() => {
    isMounted.current && !isInitial.current && setT(getT), isInitial.current = !1;
  }, [i18n, keyPrefix]);
  const ret = [t, i18n, ready];
  if (ret.t = t, ret.i18n = i18n, ret.ready = ready, ready || !ready && !useSuspense)
    return ret;
  throw new Promise((resolve) => {
    props.lng ? loadLanguages(i18n, props.lng, namespaces, () => resolve()) : loadNamespaces(i18n, namespaces, () => resolve());
  });
}
export {
  I18nContext as I,
  useTranslation as u
};
//# sourceMappingURL=useTranslation-cf39c293.js.map
